import mapboxgl from "mapbox-gl";
import mapboxGlSupported from "@mapbox/mapbox-gl-supported";

const SUPPORT_OPTIONS = { failIfMajorPerformanceCaveat: false };

function installMapboxTelemetryTeardownGuard() {
  if (typeof window === "undefined" || window.__midpointMapboxTelemetryGuard) {
    return;
  }

  window.__midpointMapboxTelemetryGuard = true;

  const suppressIfKnown = (message = "") => {
    if (String(message).includes("errorCb is not a function")) {
      return true;
    }
    return false;
  };

  window.addEventListener("error", (event) => {
    if (suppressIfKnown(event?.message || event?.error?.message)) {
      event.preventDefault();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (suppressIfKnown(event?.reason?.message || event?.reason)) {
      event.preventDefault();
    }
  });
}

installMapboxTelemetryTeardownGuard();

const SUPPORT_REASON_MESSAGES = {
  "not a browser":
    "The map can only run in a browser window. Reload this page in Chrome, Firefox, or Safari.",
  "insufficient worker support":
    "This browser blocks Web Workers, which the map needs. Open the app in Chrome, Firefox, or Safari — embedded previews often block workers.",
  "insufficient Canvas/getImageData support":
    "A privacy extension or browser setting is blocking canvas data. Disable canvas-blocking extensions for this site, or try another browser.",
  "insufficient WebGL2 support":
    "WebGL 2 is required for the map. Enable hardware acceleration (Chrome: Settings → System → Use graphics acceleration when available), then reload.",
  "insufficient ECMAScript 6 support":
    "This browser is too old for Mapbox GL. Use a current version of Chrome, Firefox, or Safari.",
};

/**
 * Check whether WebGL can be acquired (Mapbox uses WebGL for rendering).
 */
export function isWebGLAvailable() {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const canvas = document.createElement("canvas");
    const context =
      canvas.getContext("webgl2", { failIfMajorPerformanceCaveat: false }) ||
      canvas.getContext("webgl", { failIfMajorPerformanceCaveat: false }) ||
      canvas.getContext("experimental-webgl", { failIfMajorPerformanceCaveat: false });

    return Boolean(context);
  } catch {
    return false;
  }
}

function resolveMapboxSupportReason() {
  if (typeof mapboxGlSupported.notSupportedReason === "function") {
    return mapboxGlSupported.notSupportedReason(SUPPORT_OPTIONS) || null;
  }

  if (typeof mapboxgl.supported === "function" && !mapboxgl.supported(SUPPORT_OPTIONS)) {
    return "insufficient WebGL2 support";
  }

  if (!isWebGLAvailable()) {
    return "insufficient WebGL2 support";
  }

  return null;
}

function formatMapboxSupportMessage(reason) {
  if (!reason) {
    return null;
  }

  return (
    SUPPORT_REASON_MESSAGES[reason] ||
    "Mapbox GL is not supported in this browser. Enable hardware acceleration or try a different browser."
  );
}

/**
 * Returns null when Mapbox GL can run; otherwise a user-facing error message.
 */
export function getMapboxSupportError() {
  return formatMapboxSupportMessage(resolveMapboxSupportReason());
}

/** True when retrying map init will not help (browser capability issue). */
export function isPermanentMapInitError(errorMessage = "") {
  const message = String(errorMessage || "");
  if (!message) {
    return false;
  }

  if (Object.values(SUPPORT_REASON_MESSAGES).some((copy) => message === copy)) {
    return true;
  }

  return /not supported in this browser|web workers|webgl 2 is required|privacy extension|too old for mapbox/i.test(
    message
  );
}

const MIN_MAP_CONTAINER_WIDTH = 120;
const MIN_MAP_CONTAINER_HEIGHT = 200;

export function containerHasRenderableSize(container) {
  if (!container) {
    return false;
  }

  const { width, height } = container.getBoundingClientRect();
  return width >= MIN_MAP_CONTAINER_WIDTH && height >= MIN_MAP_CONTAINER_HEIGHT;
}

/** True when the mount node or its map-canvas parent has enough layout space. */
export function containerHasAdequateMapSize(container) {
  if (!container) {
    return false;
  }

  if (containerHasRenderableSize(container)) {
    return true;
  }

  const canvas = container.closest?.(".map-canvas");
  if (!canvas) {
    return false;
  }

  const { width, height } = canvas.getBoundingClientRect();
  return width >= MIN_MAP_CONTAINER_WIDTH && height >= MIN_MAP_CONTAINER_HEIGHT;
}

export function isElementVisible(element) {
  if (!element || typeof window === "undefined") {
    return false;
  }

  if (!containerHasRenderableSize(element)) {
    return false;
  }

  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden" || Number(style.opacity) === 0) {
    return false;
  }

  return true;
}

/**
 * Wait until a map container has layout and is visible (avoids WebGL init in hidden panels).
 */
export function waitForMapContainerReady(container, { maxWaitMs = 8000 } = {}) {
  return new Promise((resolve) => {
    if (!container) {
      resolve(false);
      return;
    }

    const started = Date.now();

    const check = () => {
      if (isElementVisible(container)) {
        resolve(true);
        return;
      }

      if (Date.now() - started >= maxWaitMs) {
        resolve(containerHasRenderableSize(container));
        return;
      }

      requestAnimationFrame(check);
    };

    check();
  });
}

/** Prefer the flex-sized layout root over the absolute-positioned Mapbox node. */
export function getMapLayoutTarget(container, { embedded = false } = {}) {
  if (!container) {
    return null;
  }

  if (embedded) {
    return (
      container.closest(".real-estate-experience__map") ||
      container.closest(".map-main") ||
      container.closest(".map-shell") ||
      container.closest(".map-canvas") ||
      container.parentElement ||
      container
    );
  }

  return (
    container.closest(".map-shell") ||
    container.closest(".map-canvas") ||
    container.parentElement ||
    container
  );
}

export function repositionMapboxMarkers(markers = []) {
  markers.forEach((marker) => {
    try {
      const lngLat = marker.getLngLat();
      marker.setLngLat(lngLat);
    } catch {
      // Marker may already be removed.
    }
  });
}

function paintMapPresentation(map, { container, applyLayerData, markers } = {}) {
  if (!map) {
    return false;
  }

  try {
    if (container) {
      const { width, height } = container.getBoundingClientRect();
      const w = Math.max(0, Math.round(width));
      const h = Math.max(0, Math.round(height));
      const canvas = map.getCanvas();
      if (w > 0 && h > 0 && (canvas.width !== w || canvas.height !== h)) {
        map.resize();
      } else {
        map.resize();
      }
    } else {
      map.resize();
    }

    applyLayerData?.();
    repositionMapboxMarkers(markers);

    if (typeof map.triggerRepaint === "function") {
      map.triggerRepaint();
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Resize the map, re-apply layer data, and reposition DOM markers.
 * Cluster layers need resize BEFORE setData; DOM markers need a nudge after layout settles.
 */
export function refreshMapPresentation(map, options = {}) {
  if (!map) {
    return;
  }

  const { onSettled } = options;

  paintMapPresentation(map, options);

  requestAnimationFrame(() => {
    paintMapPresentation(map, options);
    requestAnimationFrame(() => {
      paintMapPresentation(map, options);
      if (typeof map.once === "function") {
        map.once("idle", () => {
          paintMapPresentation(map, options);
          onSettled?.();
        });
      } else {
        onSettled?.();
      }
    });
  });
}

/** @deprecated Use refreshMapPresentation */
export function syncMapboxMapDisplay(map) {
  refreshMapPresentation(map);
}

/**
 * Flex layouts often settle after the map is created. Poll until canvas dimensions
 * match the layout root — mimics what opening DevTools does via window resize.
 */
export function watchMapContainerDimensions(map, container, options = {}) {
  const { durationMs = 4500, onDimensionsSynced } = options;

  if (!map || !container || typeof requestAnimationFrame !== "function") {
    return () => {};
  }

  const started = performance.now();
  let rafId = null;
  let cancelled = false;

  const tick = () => {
    if (cancelled || performance.now() - started > durationMs) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);
    const canvas = map.getCanvas();

    if (w > 0 && h > 0 && (canvas.width !== w || canvas.height !== h)) {
      refreshMapPresentation(map, { container, onSettled: onDimensionsSynced });
    }

    rafId = requestAnimationFrame(tick);
  };

  rafId = requestAnimationFrame(tick);

  return () => {
    cancelled = true;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
  };
}

/**
 * Remove a Mapbox map without surfacing telemetry teardown races.
 * Mapbox GL may fire async telemetry after remove() and throw if the map
 * unmounts before the request completes (common in React Strict Mode).
 */
export function removeMapboxMapSafely(map) {
  if (!map) {
    return;
  }

  const performRemove = () => {
    try {
      map.remove();
    } catch {
      // Map may already be removed.
    }
  };

  try {
    if (typeof map.loaded === "function" && map.loaded()) {
      performRemove();
      return;
    }

    if (typeof map.once === "function") {
      const timeoutId = window.setTimeout(performRemove, 1500);
      map.once("load", () => {
        window.clearTimeout(timeoutId);
        performRemove();
      });
      return;
    }
  } catch {
    // Fall through to immediate remove.
  }

  performRemove();
}

/**
 * Create a Mapbox map without throwing; returns { map } or { error }.
 * Attempts initialization even when the supported() pre-check fails — some embedded
 * browsers pass Map construction while the pre-check is overly strict.
 */
export function createMapboxMapSafely(options) {
  if (!containerHasAdequateMapSize(options.container)) {
    return {
      map: null,
      error: "Map container is not ready. Resize the window or try again.",
    };
  }

  try {
    const map = new mapboxgl.Map({
      failIfMajorPerformanceCaveat: false,
      antialias: false,
      preserveDrawingBuffer: false,
      ...options,
    });

    return { map, error: null };
  } catch (error) {
    const supportError = getMapboxSupportError();
    const message =
      supportError ||
      error?.message ||
      "Failed to initialize WebGL. Enable hardware acceleration or close other map tabs and reload.";

    return { map: null, error: message };
  }
}
